import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createUser,
  deleteUser,
  editUser,
  fetchUsers,
  getRoleList,
} from "../api/usersApi";
import { extractApiError } from "../api/apiErrorHandler";
import { showToast } from "components/toast";
import { userDetails } from "./authSlice";

const initialState = {
  data: {},
  loading: true,
  error: null,

  // Create
  createLoading: false,
  createError: null,

  // upload
  uploadLoading: false,
  uploadError: null,
  // Edit
  editLoading: false,
  editError: null,

  // Delete
  deleteLoading: false,
  deleteError: null,

  roleData: [],
  roleLoading: false,
  roleError: null,
};

// 🔹 Get Users
export const getUserRoleList = createAsyncThunk(
  "users/getUserRoleList",
  async (data, thunkAPI) => {
    try {
      return await getRoleList(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);
// 🔹 Get Users
export const getUsers = createAsyncThunk(
  "users/getUsers",
  async (data, thunkAPI) => {
    try {
      return await fetchUsers(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

// 🔹 Create User
export const createUsers = createAsyncThunk(
  "users/createUsers",
  async (userData, thunkAPI) => {
    try {
      return await createUser(userData);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

// 🔹 Edit User
export const editUsers = createAsyncThunk(
  "users/editUsers",
  async (userData, thunkAPI) => {
    try {
      const response = await editUser(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

// 🔹 Edit Profile
export const editProfile = createAsyncThunk(
  "users/editProfile",
  async (userData, thunkAPI) => {
    try {
      const response = await editUser(userData);
      if (response?.statuscode === 200) {
        const token = localStorage.getItem("token");
        await thunkAPI.dispatch(userDetails({ token }));
      }
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

// 🔹 Delete User
export const deleteUsers = createAsyncThunk(
  "users/deleteUsers",
  async (userData, thunkAPI) => {
    try {
      return await deleteUser(userData);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // 📥 FETCH USERS
    builder
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.data = action.payload.data;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    // 📥 FETCH ROLE
    builder
      .addCase(getUserRoleList.pending, (state) => {
        state.roleLoading = true;
        state.roleError = null;
      })
      .addCase(getUserRoleList.fulfilled, (state, action) => {
        state.roleLoading = false;
        state.roleError = null;
        state.roleData = action.payload.data;
      })
      .addCase(getUserRoleList.rejected, (state, action) => {
        state.roleLoading = false;
        state.roleError = action.payload;
      });
    // ➕ CREATE USERS
    builder
      .addCase(createUsers.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createUsers.fulfilled, (state) => {
        state.createLoading = false;
        showToast.success("User created successfully!");
      })
      .addCase(createUsers.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload || "Failed to create user";
        showToast.error(state.createError);
      });

    // ✏️ EDIT USER
    builder
      .addCase(editUsers.pending, (state) => {
        state.editLoading = true;
        state.editError = null;
      })
      .addCase(editUsers.fulfilled, (state) => {
        state.editLoading = false;
        showToast.success("User updated successfully!");
      })
      .addCase(editUsers.rejected, (state, action) => {
        state.editLoading = false;
        state.editError = action.payload || "Failed to update user";
        showToast.error(state.editError);
      });
    // ✏️ EDIT PROFILE
    builder
      .addCase(editProfile.pending, (state) => {
        state.editLoading = true;
        state.editError = null;
      })
      .addCase(editProfile.fulfilled, (state) => {
        state.editLoading = false;
        showToast.success("Profile updated successfully!");
      })
      .addCase(editProfile.rejected, (state, action) => {
        state.editLoading = false;
        state.editError = action.payload || "Failed to update Profile";
        showToast.error(state.editError);
      });

    // 🗑️ DELETE USER
    builder
      .addCase(deleteUsers.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteUsers.fulfilled, (state) => {
        state.deleteLoading = false;
        showToast.success("User deleted successfully.");
      })
      .addCase(deleteUsers.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || "Failed to delete user";
        showToast.error(state.deleteError);
      });
  },
});

export default userSlice.reducer;
